import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { message } from 'antd';
import { INVOICES_BY_VENDOR, UPDATE_INVOICE_STATUS, UPDATE_INVOICE_STATUS_BULK, NSP_CORE_INFO_BY_TYPE } from 'graphql/invoices.gql';
import { useInvoiceDownloadAudit } from '@hooks/graphql/useInvoiceDownloadAudit';
import type { VendorInvoice, SaveFeedback } from '@custom-types/invoices';
import useSession from '@hooks/useSession';
import { generateNspInvoicesZip } from '@utils/invoice-generator';
import { generateVendorInvoicesZip } from '@utils/invoice-generator/vendorInvoicesZip';
import { buildVendorInvoiceBase64 } from '@utils/invoice-generator/vendorInvoicePdfGenerator';

interface InvoicePendingChanges {
  invoiceStatus?: string;
  approvalStatus?: string;
  invoiceApproved?: boolean;
  invoiceComputedApprovalSignature?: string;
}

export const useInvoicesGraphQl = ({ monthYear }: { monthYear?: string }) => {
  const [vendorData, setVendorData] = useState<VendorInvoice[]>([]);
  const [originalData, setOriginalData] = useState<VendorInvoice[]>([]); // Track original server data
  const [pendingChanges, setPendingChanges] = useState<Record<string, InvoicePendingChanges>>({});
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback | null>(null);
  const { account } = useSession();

  // Fetch primary issuer info (for NSP invoice PDF generation)
  const { data: primaryIssuerData } = useQuery(NSP_CORE_INFO_BY_TYPE, {
    variables: { entryType: 'primary' },
    fetchPolicy: 'cache-first',
  });
  const primaryIssuer = primaryIssuerData?.nspCoreInfoByType ?? null;
  const hstRate = primaryIssuer?.hstRate ?? 0;

  // Fetch main data
  const { data, loading, refetch } = useQuery(INVOICES_BY_VENDOR, {
    variables: { monthYear },
    fetchPolicy: 'network-only',
  });

  // Initialize vendor data when data arrives
  useEffect(() => {
    if (data) {
      const vendors: VendorInvoice[] = data.invoicesByVendor;
      setVendorData(vendors);
      setOriginalData(vendors); // Store original data from server
    }
  }, [data]);

  // Refetch when monthYear changes (skip the initial mount — useQuery already
  // fires the network-only request for the initial monthYear)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (monthYear) {
      refetch({ monthYear });
    }
  }, [monthYear, refetch]);

  const [updateInvoiceStatusBulkMutation, { loading: saving }] = useMutation(UPDATE_INVOICE_STATUS_BULK);
  const { logNsp: logNspDownload, logVendor: logVendorDownload } = useInvoiceDownloadAudit();

  const handleEdit = (vendorId: number, field: string, value: unknown) => {
    // Update the vendor data in the UI (using snake_case field names)
    setVendorData((prev) =>
      prev.map((vendor) =>
        vendor.vendor_id === vendorId ? { ...vendor, [field]: value } : vendor
      )
    );

    // Map snake_case field names to camelCase for GraphQL mutation
    const fieldMapping: Record<string, string> = {
      'invoice_status': 'invoiceStatus',
      'approval_status': 'approvalStatus',
      'invoice_approved': 'invoiceApproved',
      'invoice_computed_approval_signature': 'invoiceComputedApprovalSignature',
    };

    const mutationField = fieldMapping[field] || field;

    // Track pending changes (using camelCase for mutation)
    const key = `${vendorId}_${monthYear}`;
    setPendingChanges((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [mutationField]: value,
      } as InvoicePendingChanges,
    }));
  };

  const handleSave = async () => {
    const updates = Object.entries(pendingChanges)
      .map(([key, changes]) => {
        const [vendorIdStr] = key.split('_');
        const vendorId = Number(vendorIdStr);
        const original = originalData.find((v) => v.vendor_id === vendorId);

        // Drop fields that haven't changed from the server state
        const filteredChanges: InvoicePendingChanges = {};
        if ('invoiceStatus' in changes && changes.invoiceStatus !== original?.invoice_status) {
          filteredChanges.invoiceStatus = changes.invoiceStatus;
        }
        if ('approvalStatus' in changes && changes.approvalStatus !== original?.approval_status) {
          filteredChanges.approvalStatus = changes.approvalStatus;
        }
        if ('invoiceApproved' in changes && changes.invoiceApproved !== original?.invoice_approved) {
          filteredChanges.invoiceApproved = changes.invoiceApproved;
        }
        if ('invoiceComputedApprovalSignature' in changes && changes.invoiceComputedApprovalSignature !== original?.invoice_computed_approval_signature) {
          filteredChanges.invoiceComputedApprovalSignature = changes.invoiceComputedApprovalSignature;
        }

        if (Object.keys(filteredChanges).length === 0) return null;

        return {
          ippId: vendorId,
          monthYear: monthYear!,
          ...filteredChanges,
        };
      })
      .filter((u): u is NonNullable<typeof u> => u !== null);

    if (!updates.length) {
      message.info('No changes to save.');
      return;
    }

    setSaveFeedback(null);

    try {
      const { data } = await updateInvoiceStatusBulkMutation({ variables: { updates } });
      if (data.updateInvoiceStatusBulk.success) {
        setPendingChanges({});
        await refetch();
        setSaveFeedback({
          status: 'success',
          message: `Saved ${updates.length} invoice(s) successfully!`,
        });
        message.success(`Saved ${updates.length} invoice(s) successfully!`);
      } else {
        setSaveFeedback({ status: 'error', message: 'Failed to save updates' });
        message.error('Failed to save updates');
      }
    } catch (err: any) {
      setSaveFeedback({ status: 'error', message: err.message || 'Failed to save updates' });
      message.error(err.message || 'Failed to save updates');
    }
  };

  // Batch create vendor invoices — generates each vendor's PDF client-side and sends it
  // to the backend so it can attach the same PDF to the vendor invoice email
  const handleCreateVendorInvoices = async (vendorIds: Set<number>) => {
    const vendors = vendorData.filter((v) => v.vendor_id != null && vendorIds.has(v.vendor_id));
    if (!vendors.length) return;

    setSaveFeedback(null);

    const updates = await Promise.all(vendors.map(async (v) => {
      const base = {
        ippId: v.vendor_id!,
        monthYear: monthYear!,
        invoiceStatus: 'sent',
        approvalStatus: 'Approved',
      };

      try {
        const { base64, filename } = await buildVendorInvoiceBase64({ vendor: v, primaryIssuer, monthYear: monthYear!, hstRate });
        return { ...base, invoicePdfBase64: base64, invoicePdfFilename: filename };
      } catch (err) {
        console.error(`Failed to build vendor invoice PDF for attachment (vendor ${v.vendor_id}):`, err);
        return base;
      }
    }));

    try {
      const { data: resp } = await updateInvoiceStatusBulkMutation({ variables: { updates } });
      if (resp.updateInvoiceStatusBulk.success) {
        // Optimistically update local state
        setVendorData((prev) =>
          prev.map((v) =>
            v.vendor_id != null && vendorIds.has(v.vendor_id)
              ? { ...v, invoice_status: 'sent', approval_status: 'Approved' }
              : v
          )
        );

        // Generate and download vendor invoice PDFs as a ZIP
        await generateVendorInvoicesZip({ vendors, primaryIssuer, monthYear: monthYear!, hstRate });

        vendors.forEach((v) => {
          logVendorDownload({ ippId: v.vendor_id!, monthYear: monthYear!, vendorName: v.vendor_name });
        });

        setSaveFeedback({
          status: 'success',
          message: `${vendors.length} vendor invoice(s) sent successfully.`,
        });
        message.success(`${vendors.length} vendor invoice(s) sent successfully.`);
      } else {
        setSaveFeedback({ status: 'error', message: 'Failed to update invoice statuses.' });
        message.error('Failed to update invoice statuses.');
      }
    } catch (err: any) {
      setSaveFeedback({ status: 'error', message: err.message || 'Failed to update invoice statuses.' });
      message.error(err.message || 'Failed to update invoice statuses.');
    }
  };

  // Generate NSP invoice PDFs for selected vendors, zip them, and download
  const handleCreateNspInvoices = async (vendorIds: Set<number>) => {
    const vendors = vendorData.filter((v) => v.vendor_id != null && vendorIds.has(v.vendor_id));
    if (!vendors.length) return;

    try {
      await generateNspInvoicesZip({ vendors, primaryIssuer, monthYear: monthYear!, hstRate });

      vendors.forEach((v) => {
        logNspDownload({ ippId: v.vendor_id!, monthYear: monthYear!, vendorName: v.vendor_name });
      });

      message.success(`${vendors.length} NSP invoice(s) downloaded as ZIP.`);
    } catch (err: any) {
      message.error(err.message || 'Failed to generate NSP invoices.');
    }
  };

  // True when there is at least one pending change that differs from the server state
  const hasRealChanges = Object.entries(pendingChanges).some(([key, changes]) => {
    const vendorId = Number(key.split('_')[0]);
    const original = originalData.find((v) => v.vendor_id === vendorId);
    return (
      ('invoiceStatus' in changes && changes.invoiceStatus !== original?.invoice_status) ||
      ('approvalStatus' in changes && changes.approvalStatus !== original?.approval_status) ||
      ('invoiceApproved' in changes && changes.invoiceApproved !== original?.invoice_approved) ||
      ('invoiceComputedApprovalSignature' in changes && changes.invoiceComputedApprovalSignature !== original?.invoice_computed_approval_signature)
    );
  });

  // Helper function to check if a vendor was originally approved (from saved server data)
  const isVendorApprovalPersisted = (vendorId: number): boolean => {
    const originalVendor = originalData.find((vendor) => vendor.vendor_id === vendorId);
    return Boolean(originalVendor?.invoice_approved);
  };

  return {
    data: vendorData,
    loading,
    saving,
    saveFeedback,
    setSaveFeedback,
    handleEdit,
    handleSave,
    handleCreateVendorInvoices,
    handleCreateNspInvoices,
    refetch,
    isVendorApprovalPersisted,
    hasRealChanges,
    username: account?.name || account?.username || '',
  };
};

export const useUpdateInvoiceStatus = () => {
  const [updateInvoiceStatusMutation, { loading }] = useMutation(UPDATE_INVOICE_STATUS);

  const updateInvoiceStatus = async ({
    ippId,
    monthYear,
    invoiceStatus,
    approvalStatus,
    invoiceApproved,
    invoiceComputedApprovalSignature,
  }: {
    ippId: number;
    monthYear: string;
    invoiceStatus?: string;
    approvalStatus?: string;
    invoiceApproved?: boolean;
    invoiceComputedApprovalSignature?: string;
  }) => {
    return await updateInvoiceStatusMutation({
      variables: {
        ippId,
        monthYear,
        invoiceStatus,
        approvalStatus,
        invoiceApproved,
        invoiceComputedApprovalSignature,
      },
    });
  };

  return {
    updateInvoiceStatus,
    loading,
  };
};
